import { convertToNewBaseQuantity, formatQuantity } from '../../../utils/quantityConversion';
import { ItemSummaryTable } from './ItemSummaryTable';
import { ConsumptionTable } from './ConsumptionTable';
import InventoryItemDetailModal from './InventoryItemDetailModal';
import CustomTooltip from '../../../Layout/Tooltip/CustomTooltip';
import { formatSalesValue } from '../../../utils/formatUtils';
import { selectStyles } from '../../../utils/cssUtils';
import InventoryCalculatorFilters from './InventoryCalculatorFilters';
import { unitVariations } from '../../../data/unitVariations';

const InventoryCalculationTable = ({
    consumptionData,
    fetchItemData,
    itemSearchTerm,
    setItemSearchTerm,
    filter,
    groupSearchTerm,
    setGroupSearchTerm,
    inventoryData,
    allZero,
    handleSave,
    handleOpeningClick,
    handleReceivedClick,
    handleWastageClick,
    handleFetchDetails,
    handlePhysicalClick,
    itemDetails,
    showModal,
    setShowModal,
    foodPercentage,
    foodRecipeCount,
    totalFoodItems,
    beveragePercentage,
    beverageRecipeCount,
    totalBeverageItems,
    hasPhysical,
    foodPeriod,
    beveragePeriod,
    handleFilterChange,
    endDate,
    getValidPhysical,
    savedPhysicalCounts,
    calculateDiscrepancy,
    selectedOutlet,
    baseUrl,
    token,
    areAllCountsEmpty,
}) => {
    const formattedDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);

        const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
        const day = date.getDate();
        const getOrdinal = (n) => {
            if (n > 3 && n < 21) return `${n}th`;
            switch (n % 10) {
                case 1: return `${n}st`;
                case 2: return `${n}nd`;
                case 3: return `${n}rd`;
                default: return `${n}th`;
            }
        };
        const dayWithOrdinal = getOrdinal(day);
        const month = date.toLocaleDateString("en-US", { month: "long" });
        const year = date.getFullYear();

        return `${weekday}, ${dayWithOrdinal} ${month}, ${year}`;
    };

    const currentPeriod = filter === "food" ? foodPeriod : beveragePeriod;

    const sortByNameAsc = (array, key) => {
        return [...array].sort((a, b) => {
            const nameA = (a[key] || '').toLowerCase();
            const nameB = (b[key] || '').toLowerCase();
            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;
            return 0;
        });
    };

    const sortEntriesByKeyAsc = (obj) => {
        return Object.entries(obj).sort(([keyA], [keyB]) => {
            const a = keyA.toLowerCase();
            const b = keyB.toLowerCase();
            if (a < b) return -1;
            if (a > b) return 1;
            return 0;
        });
    };

    // ── Filtered items used for both table rows and footer ─────────────────
    const filteredItems = inventoryData?.summary
        ? Object.entries(inventoryData.summary)
            .filter(([category]) =>
                (filter.toLowerCase() === 'food' && category.toLowerCase() === 'food') ||
                (filter.toLowerCase() === 'beverage' && category.toLowerCase() === 'beverage')
            )
            .flatMap(([, groups]) =>
                Object.entries(groups)
                    .filter(([groupName]) =>
                        !groupSearchTerm || groupName.toLowerCase().includes(groupSearchTerm.toLowerCase())
                    )
                    .flatMap(([, items]) =>
                        items.filter(item =>
                            !itemSearchTerm || item.name.toLowerCase().includes(itemSearchTerm.toLowerCase())
                        )
                    )
            )
        : [];

    const totalOpeningAmount = filteredItems.reduce(
        (sum, item) => sum + (parseFloat(item.opening_rate || 0) * parseFloat(item.opening || 0)),
        0
    );

    return (
        <>
            <div className="grid grid-cols-4 gap-4 mt-2.5 text-sm">
                <ItemSummaryTable
                    title="FOOD"
                    data={consumptionData.grouped_by_item_type?.Food?.itemwise_summary}
                    totalItems={totalFoodItems}
                    missingItems={foodRecipeCount}
                    percentage={foodPercentage}
                    onRowClick={fetchItemData}
                    sortByNameAsc={sortByNameAsc}
                />

                <ConsumptionTable
                    title="FOOD CONSUMPTION"
                    data={consumptionData.type_wise_consumption?.Food}
                    convertToNewBaseQuantity={convertToNewBaseQuantity}
                    unitVariations={unitVariations}
                    sortEntriesByKeyAsc={sortEntriesByKeyAsc}
                    handleFetchDetails={handleFetchDetails}
                    category="Food"
                />

                <ItemSummaryTable
                    title="BEVERAGE"
                    data={consumptionData.grouped_by_item_type?.Beverage?.itemwise_summary}
                    totalItems={totalBeverageItems}
                    missingItems={beverageRecipeCount}
                    percentage={beveragePercentage}
                    onRowClick={fetchItemData}
                    sortByNameAsc={sortByNameAsc}
                />

                <ConsumptionTable
                    title="BEVERAGE CONSUMPTION"
                    data={consumptionData.type_wise_consumption?.Beverage}
                    convertToNewBaseQuantity={convertToNewBaseQuantity}
                    unitVariations={unitVariations}
                    sortEntriesByKeyAsc={sortEntriesByKeyAsc}
                    handleFetchDetails={handleFetchDetails}
                    category="Beverage"
                />
            </div>

            <InventoryCalculatorFilters
                inventoryData={inventoryData}
                filter={filter}
                itemSearchTerm={itemSearchTerm}
                groupSearchTerm={groupSearchTerm}
                setItemSearchTerm={setItemSearchTerm}
                setGroupSearchTerm={setGroupSearchTerm}
                handleFilterChange={handleFilterChange}
                handleSave={handleSave}
                handleOpeningClick={handleOpeningClick}
                handleReceivedClick={handleReceivedClick}
                handleWastageClick={handleWastageClick}
                handlePhysicalClick={handlePhysicalClick}
                allZero={allZero}
                hasPhysical={hasPhysical}
                selectStyles={selectStyles}
                areAllCountsEmpty={areAllCountsEmpty}
            />

            <div className="mt-2.5 rounded-md border max-h-[680px] overflow-auto tiny-scrollbar">
                <table className="min-w-full divide-y divide-gray-200 text-sm text-center">
                    <thead className="bg-gray-100 bg-opacity-100 shadow-md sticky top-0 z-40">
                        <tr>
                            <th className="p-2 text-xs font-medium text-gray-700 uppercase tracking-wider">Group</th>
                            <th className="p-2 text-xs font-medium text-gray-700 uppercase tracking-wider">Item Name</th>
                            <th className="p-2 text-xs font-medium text-gray-700 uppercase tracking-wider">UOM</th>
                            <th className="px-2 text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                                Opening<br />
                                ({formattedDate(currentPeriod?.start)})
                            </th>
                            <th className="p-2 text-xs font-medium text-gray-700 uppercase tracking-wider">Received</th>
                            <th className="p-2 text-xs font-medium text-gray-700 uppercase tracking-wider">Wastage</th>
                            <th className="p-2 text-xs font-medium text-gray-700 uppercase tracking-wider">Consumption</th>
                            <th className="p-2 text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                                Current Level<br />
                                ({formattedDate(endDate)})
                            </th>
                            <th className="p-2 text-xs font-medium text-gray-700 uppercase tracking-wider">Physical</th>
                            <th className="p-2 text-xs font-medium text-gray-700 uppercase tracking-wider">Discrepancy</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                        {filteredItems.length > 0 ? (
                            // Re-render grouped with groupName available
                            inventoryData?.summary
                                ? Object.entries(inventoryData.summary)
                                    .filter(([category]) =>
                                        (filter.toLowerCase() === 'food' && category.toLowerCase() === 'food') ||
                                        (filter.toLowerCase() === 'beverage' && category.toLowerCase() === 'beverage')
                                    )
                                    .flatMap(([category, groups]) =>
                                        Object.entries(groups)
                                            .filter(([groupName]) =>
                                                !groupSearchTerm || groupName.toLowerCase().includes(groupSearchTerm.toLowerCase())
                                            )
                                            .flatMap(([groupName, items]) =>
                                                items
                                                    .filter(item =>
                                                        !itemSearchTerm || item.name.toLowerCase().includes(itemSearchTerm.toLowerCase())
                                                    )
                                                    .map((item) => {
                                                        const key = `${category}-${groupName}-${item.name}`;
                                                        const physicalCount = getValidPhysical(item.name, savedPhysicalCounts);

                                                        return (
                                                            <tr key={key} className="hover:bg-gray-100">
                                                                <td className="p-2">{groupName}</td>
                                                                <td
                                                                    className="p-2 hover:cursor-pointer hover:underline"
                                                                    onClick={() => handleFetchDetails(item.name, category)}
                                                                >
                                                                    {item.name}
                                                                </td>
                                                                <td className="p-2">{item.uom}</td>
                                                                <td className="p-2">
                                                                    <CustomTooltip
                                                                        content={
                                                                            <div className='font-semibold'>
                                                                                Amount: {formatSalesValue(item.opening_rate * item.opening)}
                                                                            </div>
                                                                        }
                                                                    >
                                                                        <span className="cursor-pointer hover:underline">
                                                                            {formatQuantity(item, item.opening)}
                                                                        </span>
                                                                    </CustomTooltip>
                                                                </td>
                                                                <td className="p-2">{formatQuantity(item, item.total_received)}</td>
                                                                <td className="p-2">{formatQuantity(item, item.total_wasted)}</td>
                                                                <td className="p-2">
                                                                    {formatQuantity(item, item.total_consumed)}
                                                                </td>
                                                                <td className="p-2">
                                                                    <CustomTooltip
                                                                        content={
                                                                            <div className='font-semibold'>
                                                                                Amount: {formatSalesValue(item.opening_rate * item.closing_balance)}
                                                                            </div>
                                                                        }
                                                                    >
                                                                        <span className="cursor-pointer hover:underline">
                                                                            {formatQuantity(item, item.closing_balance)}
                                                                        </span>
                                                                    </CustomTooltip>
                                                                </td>
                                                                <td className="p-2">
                                                                    {formatQuantity(item, physicalCount)}
                                                                </td>
                                                                <td className="p-2">
                                                                    {formatQuantity(item, calculateDiscrepancy(physicalCount, item.closing_balance))}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                            )
                                    )
                                : null
                        ) : (
                            <tr>
                                <td colSpan={10} className="px-3 py-4 text-gray-500">No data available</td>
                            </tr>
                        )}
                    </tbody>

                    {filteredItems.length > 0 && (
                        <tfoot className="bg-gray-100 bg-opacity-100 shadow-md sticky bottom-0 z-40">
                            <tr>
                                <td></td>
                                <td className="px-3 py-1.5 text-xs font-semibold text-gray-800 uppercase tracking-wide">
                                    Items: <span className='text-sm'>{filteredItems.length}</span>
                                </td>
                                <td></td>
                                <td className="px-3 py-1.5 font-semibold text-gray-800 tracking-wide">
                                    {formatSalesValue(totalOpeningAmount)}
                                </td>
                                <td colSpan={6} />
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            <InventoryItemDetailModal
                showModal={showModal}
                setShowModal={setShowModal}
                itemDetails={itemDetails}
                selectedOutlet={selectedOutlet}
                baseUrl={baseUrl}
                token={token}
            />
        </>
    );
};

export default InventoryCalculationTable;